import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackPageView, getSessionId, getUserId } from "../utils/pageTracking";

const CheckoutSuccess: React.FC = () => {
    useEffect(() => {
        // Track page view
        trackPageView('CheckoutSuccess', getSessionId(), getUserId());
        
        // Scroll to top when page loads
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-[#fafaf3] pt-16">
            {/* Hero Section */}
            <section className="w-full bg-[#c3d3ab] text-white py-16">
                <div className="container mx-auto px-6 text-center">
                    <div className="text-6xl mb-6">🎉</div>
                    <h1 className="font-mono text-[#b05e2c] text-4xl md:text-5xl lg:text-6xl mb-6">
                        Welcome to 1 Earth Composting!
                    </h1>
                    <p className="text-xl md:text-2xl max-w-3xl mx-auto text-black">
                        Thank you for signing up! We're excited to help you start your composting journey.
                    </p>
                </div>
            </section>

            {/* Success Details Section */}
            <section className="py-12">
                <div className="container mx-auto px-6 max-w-4xl text-center">
                    <h2 className="text-3xl font-bold text-[#b05e2c] mb-8">
                        What Happens Next?
                    </h2>
                    
                    <div className="grid md:grid-cols-3 gap-8 mb-12">
                        <div className="bg-white rounded-lg p-6 shadow-lg">
                            <div className="text-4xl mb-4">📧</div>
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-3">
                                Confirmation Email
                            </h3>
                            <p className="text-gray-700">
                                You'll receive a confirmation email with all the details about your subscription and next steps.
                            </p>
                        </div>
                        
                        <div className="bg-white rounded-lg p-6 shadow-lg">
                            <div className="text-4xl mb-4">🪣</div>
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-3">
                                Bucket Delivery
                            </h3>
                            <p className="text-gray-700">
                                We'll coordinate the delivery of your compost bucket and provide instructions on how to get started.
                            </p>
                        </div>
                        
                        <div className="bg-white rounded-lg p-6 shadow-lg">
                            <div className="text-4xl mb-4">🌱</div>
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-3">
                                Start Composting
                            </h3>
                            <p className="text-gray-700">
                                Begin collecting your compostable materials and help us create a more sustainable future!
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Information Section */}
            <section className="bg-[#c3d3ab] py-12">
                <div className="container mx-auto px-6 max-w-4xl">
                    <h2 className="text-3xl font-bold text-[#b05e2c] mb-8 text-center">
                        Important Information
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white/90 rounded-lg p-6">
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-3">
                                📅 Service Schedule
                            </h3>
                            <ul className="text-gray-700 space-y-2">
                                <li>• Curbside pickups: Weekly on your scheduled day</li>
                                <li>• Drop-off locations: Available 24/7</li>
                                <li>• Bucket exchanges: Clean bucket every pickup</li>
                            </ul>
                        </div>
                        
                        <div className="bg-white/90 rounded-lg p-6">
                            <h3 className="text-xl font-semibold text-[#b05e2c] mb-3">
                                💡 Tips for Success
                            </h3>
                            <ul className="text-gray-700 space-y-2">
                                <li>• Keep food scraps in your bucket only</li>
                                <li>• No large bones or animal waste</li>
                                <li>• Coffee grounds and tea bags are welcome</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Action Section */}
            <section className="py-12">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold text-[#b05e2c] mb-6">
                        Ready to Explore More?
                    </h2>
                    <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
                        Learn more about composting or explore our other services while you wait for your bucket.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/what-is-composting"
                            className="inline-block bg-[#b05e2c] text-white px-8 py-4 rounded-lg hover:bg-[#924d24] transition-colors font-semibold text-lg"
                        >
                            Learn About Composting
                        </Link>
                        <Link
                            to="/faqs"
                            className="inline-block bg-[#c3d3ab] text-[#b05e2c] px-8 py-4 rounded-lg hover:bg-[#b5c79e] transition-colors font-semibold text-lg border-2 border-[#b05e2c]"
                        >
                            View FAQs
                        </Link>
                        <Link
                            to="/"
                            className="inline-block bg-gray-600 text-white px-8 py-4 rounded-lg hover:bg-gray-700 transition-colors font-semibold text-lg"
                        >
                            Back to Home
                        </Link>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="bg-[#fafaf3] py-8 border-t">
                <div className="container mx-auto px-6 text-center">
                    <p className="text-gray-600 mb-4">
                        Have questions about your subscription?
                    </p>
                    <Link
                        to="/contact-us"
                        className="text-[#b05e2c] hover:text-orange-700 transition-colors font-semibold underline"
                    >
                        Contact us for support
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default CheckoutSuccess;
